/**
 * RoomMenuContent renders the content inside the room menu dialog.
 *
 * Why: The dialog content has complex layout with header, close button, and
 * action buttons. Extracting it keeps RoomMenu focused on dialog state and
 * controller input handling.
 *
 * When to use: Used internally by RoomMenu as the Dialog.Content children.
 */
import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTheme } from '../../../contexts/ThemeContext'
import { IconButton } from '../../library/IconButton/IconButton'
import { MenuButton } from '../../library/MenuButton/MenuButton'
import { CloseIcon } from '../../library/Icons/CloseIcon'
import { GearIcon } from '../../library/Icons/GearIcon'
import { CheckIcon } from '../../library/Icons/CheckIcon'
import { CopyIcon } from '../../library/Icons/CopyIcon'
import { LogoutIcon } from '../../library/Icons/LogoutIcon'
import { VideoconferenceToggle } from '../../library/VideoconferenceToggle/VideoconferenceToggle'
import { RoomAccessControls } from '../RoomAccessControls/RoomAccessControls'
import { RoomModeToggle } from '../RoomModeToggle/RoomModeToggle'
import { HostTermPicker } from '../HostTermPicker/HostTermPicker'
import { roomMenuTestIds } from './roomMenu.testIds'

export interface RoomMenuContentProps {
  /** Room title to display */
  roomTitle: string
  /** Owner name to display */
  ownerName: string
  /** Whether the current user is the owner of the room */
  isOwner?: boolean
  /** Room cover/thumbnail image URL */
  imageUrl?: string | null
  /** Whether Daily.co videoconferencing is enabled for this room */
  videoEnabled?: boolean
  /** Whether non-owners are currently denied access. */
  isClosed?: boolean
  /** ISO-8601 instant when the room opens automatically, or null for an indefinite closure. */
  opensAt?: string | null
  /** Whether the room is in managed mode (only hosts start or end games). */
  managed?: boolean
  /** Owner-only: switch managed mode. */
  onSetManaged?: (managed: boolean) => Promise<void>
  /** What the room calls a host, such as "Teacher". */
  hostTerm?: string
  /** Owner-only: name the host role. */
  onSetHostTerm?: (hostTerm: string) => Promise<void>
  /** Whether participants see only the owner's curated games. */
  curated?: boolean
  /** Owner-only: switch curation. */
  onSetCurated?: (curated: boolean) => Promise<void>
  /** Callback to update room metadata on the backend */
  onUpdateRoom?: (updates: { displayName?: string; imageUrl?: string; ownerName?: string; videoEnabled?: boolean; isClosed?: boolean; opensAt?: string | null }) => Promise<void>
  /** Handler for copy link action */
  onCopyLink: () => void
  /** Handler for leave room action */
  onLeaveRoom: () => void
  /** Whether the link was recently copied */
  copied: boolean
  /** Index of the focused menu item (0 = copy, 1 = leave) */
  focusedIndex: number | null
  /** Ref for the copy link button */
  copyLinkButtonRef: React.RefObject<HTMLButtonElement>
  /** Ref for the leave room button */
  leaveRoomButtonRef: React.RefObject<HTMLButtonElement>
}

const EditIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
  </svg>
)

export function RoomMenuContent({
  roomTitle,
  ownerName,
  isOwner = false,
  imageUrl = null,
  videoEnabled = true,
  isClosed = false,
  opensAt = null,
  managed = false,
  curated = false,
  onSetCurated,
  onSetManaged,
  hostTerm = 'Host',
  onSetHostTerm,
  onUpdateRoom,
  onCopyLink,
  onLeaveRoom,
  copied,
  focusedIndex,
  copyLinkButtonRef,
  leaveRoomButtonRef,
}: RoomMenuContentProps) {
  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()
  const accentColor = appColorShades.main

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = React.useState(false)
  const [editName, setEditName] = React.useState(roomTitle)
  const [editOwnerName, setEditOwnerName] = React.useState(ownerName)
  const [editImageUrl, setEditImageUrl] = React.useState(imageUrl || '')
  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [scheduledOpening, setScheduledOpening] = React.useState('')
  const [isPickingHostTerm, setIsPickingHostTerm] = React.useState(false)

  React.useEffect(() => {
    const date = opensAt ? new Date(opensAt) : new Date(Date.now() + 60 * 60 * 1000)
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000)
    setScheduledOpening(localDate.toISOString().slice(0, 16))
  }, [opensAt])

  const handleToggleVideo = async (next: boolean) => {
    if (!onUpdateRoom) return
    try {
      await onUpdateRoom({ videoEnabled: next })
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update room')
    }
  }

  const updateAccess = async (updates: { isClosed: boolean; opensAt: string | null }) => {
    if (!onUpdateRoom) return
    setSaveError(null)
    setIsSaving(true)
    try {
      await onUpdateRoom(updates)
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update room access')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCloseUntil = () => {
    const opening = new Date(scheduledOpening)
    if (!Number.isFinite(opening.getTime()) || opening.getTime() <= Date.now()) {
      setSaveError('Choose a future opening time')
      return
    }
    void updateAccess({ isClosed: true, opensAt: opening.toISOString() })
  }

  React.useEffect(() => {
    setEditName(roomTitle)
  }, [roomTitle])

  React.useEffect(() => {
    setEditOwnerName(ownerName)
  }, [ownerName])

  React.useEffect(() => {
    setEditImageUrl(imageUrl || '')
  }, [imageUrl])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 1024 * 1024) {
      setSaveError('Image must be smaller than 1MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') {
        setEditImageUrl(event.target.result)
        setSaveError(null)
      }
    }
    reader.onerror = () => {
      setSaveError('Error reading file')
    }
    reader.readAsDataURL(file)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!onUpdateRoom) return
    setSaveError(null)
    setIsSaving(true)
    try {
      await onUpdateRoom({
        displayName: editName.trim() || undefined,
        imageUrl: editImageUrl.trim() || undefined,
        ownerName: editOwnerName.trim() || undefined,
      })
      setIsEditing(false)
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update room')
    } finally {
      setIsSaving(false)
    }
  }

  if (isEditing) {
    return (
      <form onSubmit={handleSave} className="flex flex-col gap-4 h-full">
        <div className="flex items-center justify-between border-b border-basic pb-3">
          <h3 className="text-sm font-bold text-primary">Edit Room Info</h3>
          <button
            type="button"
            onClick={() => {
              setIsEditing(false)
              setSaveError(null)
            }}
            className="text-xs font-semibold text-muted hover:text-primary transition-colors focus:outline-none"
          >
            Cancel
          </button>
        </div>

        {saveError && (
          <div className="text-xs text-danger bg-danger-bg/10 border border-danger-bg/25 p-2 rounded">
            {saveError}
          </div>
        )}

        <div className="flex flex-col gap-3 flex-1 overflow-y-auto scrollbar-none pr-1">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-muted uppercase tracking-wider">Room Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="p-2 border border-basic rounded bg-surface text-primary text-sm focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
              required
              maxLength={50}
              placeholder="e.g. Yuri's Chess Club"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-muted uppercase tracking-wider">Owner Name</label>
            <input
              type="text"
              value={editOwnerName}
              onChange={(e) => setEditOwnerName(e.target.value)}
              className="p-2 border border-basic rounded bg-surface text-primary text-sm focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
              required
              maxLength={50}
              placeholder="e.g. Yuri"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-muted uppercase tracking-wider">Room Image</label>
            {editImageUrl && (
              <div className="w-full h-20 rounded overflow-hidden border border-basic relative">
                <img src={editImageUrl} alt="Preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setEditImageUrl('')}
                  className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/85 text-white rounded-full transition-colors cursor-pointer focus:outline-none"
                  title="Remove Image"
                >
                  <CloseIcon width={10} height={10} />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 py-2 text-xs font-bold border-2 border-dashed border-muted/30 hover:border-accent hover:bg-surface-hover/10 rounded flex items-center justify-center gap-1.5 text-muted hover:text-primary transition-all cursor-pointer focus:outline-none"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                </svg>
                Upload File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            <div className="text-[10px] text-muted leading-tight mt-1">
              Select any image file (PNG, JPG, SVG, WebP) up to 1MB.
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t border-basic">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="flex-1 py-2 text-sm font-semibold border border-basic rounded hover:bg-surface-hover text-muted hover:text-primary transition-all cursor-pointer focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 py-2 text-sm font-semibold rounded text-white bg-accent hover:brightness-110 disabled:opacity-50 transition-all cursor-pointer focus:outline-none"
            style={{ backgroundColor: accentColor }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    )
  }

  return (
    <>
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Dialog.Title className="text-lg font-bold text-primary break-words flex-1 leading-tight">
              {roomTitle}
            </Dialog.Title>
            {isOwner && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 hover:bg-surface-hover rounded transition-colors text-muted hover:text-primary cursor-pointer focus:outline-none flex-shrink-0"
                title="Edit name"
              >
                <EditIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-sm text-muted break-words">
            {ownerName}'s room
          </p>
        </div>
        <Dialog.Close asChild>
          <IconButton
            className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus flex-shrink-0"
            aria-label="Close"
            style={accentColor ? { color: accentColor } : undefined}
            onMouseEnter={(e) => {
              if (accentColor) {
                e.currentTarget.style.backgroundColor = accentColor
                e.currentTarget.style.color = 'white'
              }
            }}
            onMouseLeave={(e) => {
              if (accentColor) {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = accentColor
              }
            }}
            icon={<CloseIcon width={16} height={16} />}
          />
        </Dialog.Close>
      </div>

      {saveError && (
        <div className="text-xs text-danger bg-danger-bg/10 border border-danger-bg/25 p-2 rounded mb-4">
          {saveError}
        </div>
      )}

      {imageUrl ? (
        <div className="w-full h-32 rounded-lg overflow-hidden mb-4 border border-basic relative group">
          <img src={imageUrl} alt={roomTitle} className="w-full h-full object-cover" />
          {isOwner && (
            <button
              onClick={() => setIsEditing(true)}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors cursor-pointer focus:outline-none"
              title="Edit image"
            >
              <EditIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ) : isOwner ? (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full h-24 border-2 border-dashed border-muted/30 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-accent hover:bg-surface-hover/10 transition-colors text-muted hover:text-primary mb-4 p-2 cursor-pointer focus:outline-none"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
          <span className="text-xs font-semibold">Add Room Image</span>
        </button>
      ) : null}

      {isOwner && (
        <div className="flex flex-col gap-4 py-3 border-y border-basic mb-4">
          <VideoconferenceToggle videoEnabled={videoEnabled} onToggle={handleToggleVideo} testId={roomMenuTestIds.videoToggle} />
          {onSetManaged && (
            <RoomModeToggle
              label="Managed mode"
              description={`Only people marked ${hostTerm} can start or end games.`}
              checked={managed}
              onToggle={onSetManaged}
              testId={roomMenuTestIds.managedToggle}
              accessory={onSetHostTerm && (
                <IconButton
                  data-testid={roomMenuTestIds.hostTermButton}
                  aria-label="Choose what to call hosts"
                  aria-expanded={isPickingHostTerm}
                  title="Choose what to call hosts"
                  onClick={() => setIsPickingHostTerm((open) => !open)}
                  className="rounded p-1 text-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                  icon={<GearIcon width={16} height={16} />}
                />
              )}
            >
              {onSetHostTerm && isPickingHostTerm && (
                <HostTermPicker hostTerm={hostTerm} onSave={onSetHostTerm} onDone={() => setIsPickingHostTerm(false)} />
              )}
            </RoomModeToggle>
          )}
          {onSetCurated && (
            <RoomModeToggle
              label="Curated games"
              description="Everyone else sees only the games you pick, in your order."
              checked={curated}
              onToggle={onSetCurated}
              testId={roomMenuTestIds.curatedToggle}
            />
          )}
          <RoomAccessControls
            isClosed={isClosed}
            opensAt={opensAt}
            scheduledOpening={scheduledOpening}
            isSaving={isSaving}
            onScheduledOpeningChange={setScheduledOpening}
            onOpen={() => void updateAccess({ isClosed: false, opensAt: null })}
            onCloseIndefinitely={() => void updateAccess({ isClosed: true, opensAt: null })}
            onCloseUntil={handleCloseUntil}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <MenuButton
            ref={copyLinkButtonRef}
            testId={roomMenuTestIds.copyLinkButton}
            onClick={onCopyLink}
            icon={copied ? CheckIcon : CopyIcon}
            isFocused={focusedIndex === 0}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </MenuButton>

          <MenuButton
            ref={leaveRoomButtonRef}
            testId={roomMenuTestIds.leaveRoomButton}
            onClick={onLeaveRoom}
            icon={LogoutIcon}
            isFocused={focusedIndex === 1}
          >
            Leave Room
          </MenuButton>
        </div>
      </div>
    </>
  )
}
